import { Fragment, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { DataLakeEnums, DataLakeQuery } from 'dashboard-services/api/dataLake';
import { DataPrepQuery } from 'dashboard-services/api/dataPrep';
import { DataScienceQuery } from 'dashboard-services/api/dataScience';
import { FormulaQuery } from 'dashboard-services/api/formula';
import Query from 'dashboard-services/api/Query';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import { loginActions } from 'primary-components';

import { basicPipelineDashboardActions } from 'actions/modules/datascience/dashboard';
import { settingsMyAccountActions } from 'actions/modules/myAccount';
import getApiConfig from 'api/ApiConfig';
import { routing } from 'routing';

import ResultBlock from '../ResultBlock';
import InsightRow from './InsightRow';
import StatusRow from './StatusRow';

import { useQueries } from '@tanstack/react-query';
import { When } from 'react-if';

const RECENT_TYPES = settingsMyAccountActions.RECENT_TYPES
// Formula statuses come from the Monitor's component status search, which has no STATUS_TYPE.
const FORMULA_STATUS_TYPE = basicPipelineDashboardActions.MONITORING_TYPE.FORMULA.value
const useGetRecents = ({ recents, getQueryBuilder }) => {
  const queries = useMemo(() => recents.map(recent => {
    if(recent.type === RECENT_TYPES.PIPELINE) {
      return ({
        ...DataScienceQuery.QUERIES.getPipelineVersions(
          getQueryBuilder,
          {
            pipelineName: recent.name,
            groupName: recent.groupName,
            query: "",
            size: 1
          }
        ),
        select: response => response?.items?.[0] ? recent: undefined,
        enabled: !!recent.name && !!recent.groupName
      })
    } else if(recent.type === RECENT_TYPES.GROUP) {
      return ({
        ...DataLakeQuery.QUERIES.getGroup(
          getQueryBuilder,
          {
            groupName: recent.name
          }
        ),
        select: response => response ? recent : undefined,
        enabled: !!recent.name
      })
    } else if(recent.type === RECENT_TYPES.DATA_PREP) {
      return ({
        ...DataPrepQuery.QUERIES.getVersionsByName(
          getQueryBuilder,
          {
            name: recent.name,
            size: 1
          }
        ),
        select: response => response?.items?.[0] ? recent: undefined,
        enabled: !!recent.name
      })
    } else if(recent.type === RECENT_TYPES.INSIGHT) {
      return ({
        ...DataLakeQuery.QUERIES.searchFiles(
          getQueryBuilder,
          {
            query: `groupName=${recent.groupName}&name=${recent.name}.json&type=${DataLakeEnums.FILE_TYPE.INSIGHT}&latest=true`,
            size: 1
          }
        ),
        select: response => response?.items?.[0] ? recent: undefined,
        enabled: !!recent.name && !!recent.groupName
      })
    } else if(recent.type === RECENT_TYPES.FORMULA) {
      return ({
        ...FormulaQuery.QUERIES.getByName(
          getQueryBuilder,
          {
            groupName: recent.groupName,
            name: recent.name
          }
        ),
        select: response => response ? recent : undefined,
        enabled: !!recent.name && !!recent.groupName
      })
    } else {
      return Promise.resolve()
    }
  }), [getQueryBuilder, recents]),
  combine = useCallback(results => ({
    data: results.map(result => result.data).filter(data => data !== undefined),
    isPending: results.some(result => result.isPending),
  }), [])
  return useQueries({
    queries,
    combine
  })
}

const ItemBlock = ({ currentRecents, query = undefined, setExpandedFalse, title, titleIcon }) => {
  const dispatch = useDispatch(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withShouldDisplayError(false)
        , [getApiConfigLocal]),
        { data: recents } = useGetRecents({ recents: currentRecents, getQueryBuilder }),
        recentGroups = useMemo(() => recents.filter(r => r.type === RECENT_TYPES.GROUP && r.name && r.name !== "undefined"), [recents]),
        recentPipelines = useMemo(() => recents.filter(r => r.type === RECENT_TYPES.PIPELINE && r.name && r.name !== "undefined"), [recents]),
        recentDataPreps = useMemo(() => recents.filter(r => r.type === RECENT_TYPES.DATA_PREP && r.name && r.name !== "undefined"), [recents]),
        recentInsights = useMemo(() => recents.filter(r => r.type === RECENT_TYPES.INSIGHT && r.name && r.name !== "undefined"), [recents]),
        recentFormulas = useMemo(() => recents.filter(r => r.type === RECENT_TYPES.FORMULA && r.name && r.name !== "undefined"), [recents]),
        groupsAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataLake.routing.groups.name, "R", routing.dataLake.name)), [dispatch]),
        dataPrepsAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataStorage.routing.list.name, "R", routing.dataStorage.name)), [dispatch]),
        pipelinesAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.list.hashes.pipelines, "R", routing.dataScience.name)), [dispatch]),
        insightsAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.insight.name, "R")), [dispatch]),
        // A formula opens on the Monitor, so it needs access to it.
        formulasAreVisible = useMemo(() => dispatch(loginActions.hasAccessTo(routing.dataScience.routing.dashboard.name, "R", routing.dataScience.name)), [dispatch])
        
  return (
    <Fragment>
      <When condition={recents?.length > 0}>
        {() => (
          <div className="search-all__content__results__title">
            <div className="search-all__content__results__title__icon">
              {titleIcon}
            </div>
            <p>{title}</p>
          </div>
        )}
      </When>
      <When condition={groupsAreVisible && recentGroups?.length > 0}>
        {() => (
          <ResultBlock
              closeSearch={setExpandedFalse}
              isFetching={false}
              isRecent
              itemRenderer={recent => (
                <StatusRow
                    key={recent.name + recent.groupName + StatusesEnums.STATUS_TYPE.ETL}
                    name={recent.name}
                    type={StatusesEnums.STATUS_TYPE.ETL}
                />
              )}
              query={query}
              results={recentGroups}
              title="Groups"
          />
        )}
      </When>
      <When condition={dataPrepsAreVisible && recentDataPreps?.length > 0}>
        {() => (
          <ResultBlock
              closeSearch={setExpandedFalse}
              isFetching={false}
              isRecent
              itemRenderer={recent => (
                <StatusRow
                    groupName={recent.groupName}
                    key={recent.name + recent.groupName + StatusesEnums.STATUS_TYPE.DATAPREP}
                    name={recent.name}
                    optional={recent.groupName}
                    type={StatusesEnums.STATUS_TYPE.DATAPREP}
                />
              )}
              query={query}
              results={recentDataPreps}
              title="Data preps"
          />
        )}
      </When>
      <When condition={pipelinesAreVisible && recentPipelines?.length > 0}>
        {() => (
          <ResultBlock
              closeSearch={setExpandedFalse}
              isFetching={false}
              isRecent
              itemRenderer={recent => (
                <StatusRow
                    key={recent.name + recent.groupName + StatusesEnums.STATUS_TYPE.PIPELINE}
                    name={recent.name}
                    optional={recent.groupName}
                    type={StatusesEnums.STATUS_TYPE.PIPELINE}
                />
              )}
              query={query}
              results={recentPipelines}
              title="Pipelines"
          />
        )}
      </When>
      <When condition={formulasAreVisible && recentFormulas?.length > 0}>
        {() => (
          <ResultBlock
              closeSearch={setExpandedFalse}
              isFetching={false}
              isRecent
              itemRenderer={recent => (
                <StatusRow
                    groupName={recent.groupName}
                    key={recent.name + recent.groupName + FORMULA_STATUS_TYPE}
                    name={recent.name}
                    optional={recent.groupName}
                    type={FORMULA_STATUS_TYPE}
                />
              )}
              query={query}
              results={recentFormulas}
              title="Formulas"
          />
        )}
      </When>
      <When condition={insightsAreVisible && recentInsights?.length > 0}>
        {() => (
          <ResultBlock
              closeSearch={setExpandedFalse}
              isFetching={false}
              isRecent
              itemRenderer={recent => (
                <InsightRow
                    groupName={recent.groupName}
                    key={recent.name + recent.groupName + "INSIGHT"}
                    name={recent.name}
                    optional={recent.groupName}
                />
              )}
              query={query}
              results={recentInsights}
              title="Insights"
          />
        )}
      </When>
    </Fragment>
  )
}

ItemBlock.propTypes = {
  query: PropTypes.string,
  setExpandedFalse: PropTypes.func.isRequired
}

export default ItemBlock;